import React, { memo } from 'react'
import { Form } from 'antd'
import { Trans, useLingui } from '@lingui/react/macro'

import LyriceActionBtn from './_comp/LyriceActionBtn'
import { MoreIdeaInput } from './_comp/MoreIdeaInput'

import type { FormInstance } from 'antd/lib'
import type { MusicGeneratorFormValuesNew } from '@/pages/tad.ai/ai-music-generator/_types/form'

import EnhanceTextArea from '@/components/EnhanceTextArea'
import {
  GenerateLyricsSmartType,
  useAiMusicGeneratorStoreFromZustand,
} from '@/pages/tad.ai/ai-music-generator/_store/createStore'
import { AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH } from '@/pages/tad.ai/ai-music-generator/const'


import { cls } from 'tagged-classnames-free';
export interface IAiLyriceProps {
  lyrics: string
  onChangeLyrics: (value: string) => void
  form: FormInstance<MusicGeneratorFormValuesNew>
}

const AiLyrice: React.FC<IAiLyriceProps> = (props: IAiLyriceProps) => {
  const { lyrics, form, onChangeLyrics } = props
  const { updateAiIdeasModalOpen, generateLyricsSmart, isStream } =
    useAiMusicGeneratorStoreFromZustand()
  const thinking = Form.useWatch('thinking', form)
  const imageUrl = Form.useWatch('imageUrl', form)
  const prompt = Form.useWatch('prompt', form)
  const promptTabsMode = Form.useWatch('promptTabsMode', form)
  const { t } = useLingui()

  const PolishPromptMap = [
    { class: 'i-cus--tad-expand', key: 'expand', text: t`Expand` },
    { class: 'i-cus--tad-condense', key: 'condense', text: t`Condense` },
  ]

  const handlePolishPrompt = (item: string) => {
    generateLyricsSmart({
      type: GenerateLyricsSmartType.POLISH,
      formType: promptTabsMode,
      prompt: prompt || '',
      thinking,
      imageUrl,
      lyrics,
      polishPrompt: item,
      generateLyricsFinishCallback: () => {},
      updateLyricsRealTimeCallback: (lyrics) => {
        // onChangeLyrics(lyrics)
      },
    })
  }

  return (
    <Form.Item name='lyrics'>
      <div className={cls`space-y-2`}>
        {/* Header */}
        <div className={cls`flex items-center justify-between`}>
          <div className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Lyrics</Trans>
          </div>
          {/* <div className='flex size-5 cursor-pointer items-center justify-center'>
            <span className='i-com--history size-4'></span>
          </div> */}
        </div>

        {/* Text Area */}
        <EnhanceTextArea
          value={lyrics}
          onChange={(e) => onChangeLyrics(e.target.value)}
          placeholder={
            promptTabsMode === 'smart'
              ? t`Generated lyrics will appear here...`
              : t`Enter your own lyrics here`
          }
          rows={12}
          className={cls`
            bg-f-bg-elevated
            border-f-border-secondary
            resize-none
            rounded-lg
          `}
          containerClassName={cls`
            bg-f-bg-elevated
            border-f-border-secondary
            hover:bg-f-bg-elevated
            overflow-hidden
            rounded-lg
          `}
          count={{
            show: true,
            max: AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH,
            strategy: (text) => {
              return text.length
            },
          }}
          showClear
          disabledClear={isStream}
          autoScrollToBottom
        />

        {/* Action Buttons */}
        <div className={cls`flex flex-wrap justify-between`}>
          <div className='flex items-center gap-5'>
            {PolishPromptMap.map((item) => {
              const btnDisabled: boolean =
                isStream ||
                (item.key === 'expand' &&
                  lyrics.length > AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH)

              return (
                <LyriceActionBtn
                  disabled={btnDisabled}
                  key={item.key}
                  icon={item.class}
                  text={item.text}
                  onClick={() => handlePolishPrompt(item.text)}
                />
              )
            })}
          </div>
          <div className='flex items-center gap-5'>
            {promptTabsMode === 'smart' && (
              <LyriceActionBtn
                disabled={isStream}
                icon='i-cus--tad-ai-generated'
                text={t`AI Ideas`}
                onClick={() => {
                  if (isStream) return
                  updateAiIdeasModalOpen(true)
                }}
              />
            )}
          </div>
        </div>

        {/** more ideas */}
        <MoreIdeaInput form={form} />
      </div>
    </Form.Item>
  )
}

const MemoizedAiLyrice = memo(AiLyrice)
MemoizedAiLyrice.displayName = 'AiLyrice'

export default MemoizedAiLyrice
